import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useSyncExternalStore, type PropsWithChildren } from "react";
import { useBlocker, useLocation } from "react-router-dom";
import { fetchTrackingSettings } from "./marketing-api";
import { loadMarketingScripts, reloadMarketingDocument, stopMarketingScripts } from "./marketing-scripts";
import { captureMarketingAttribution, emitBrowserMarketingEvent, getMarketingConsent, getMarketingStorageRevision, publicTrackingPath, safeBrowserTrackingUrl, setMarketingSettings, subscribeMarketing } from "./marketing-runtime";

export function MarketingProvider({ children }: PropsWithChildren) {
  const location = useLocation();
  const publicPath = publicTrackingPath(location.pathname);
  const consent = useSyncExternalStore(subscribeMarketing, getMarketingConsent, () => null);
  const remoteRevision = useSyncExternalStore(subscribeMarketing, getMarketingStorageRevision, () => 0);
  const handledRemoteRevision = useRef(remoteRevision);
  const loaded = useRef(false);
  const documentId = useRef(crypto.randomUUID());
  const config = useQuery({
    queryKey: ["storefront", "tracking"],
    queryFn: ({ signal }) => fetchTrackingSettings(signal),
    enabled: publicPath !== null, staleTime: 60_000, retry: false,
  });
  const blocker = useBlocker(({ nextLocation }) => loaded.current &&
    !safeBrowserTrackingUrl(window.location.origin + nextLocation.pathname + nextLocation.search + nextLocation.hash));
  useEffect(() => {
    if (blocker.state === "blocked") window.location.assign(blocker.location.pathname + blocker.location.search + blocker.location.hash);
  }, [blocker]);
  useEffect(() => {
    if (config.data) setMarketingSettings(config.data);
  }, [config.data]);
  useEffect(() => {
    if (config.data) captureMarketingAttribution(location.pathname + location.search);
  }, [config.data, consent, location.pathname, location.search]);
  useEffect(() => {
    if (config.data?.enabled && publicPath !== null) {
      loaded.current = loadMarketingScripts(config.data) || loaded.current;
      emitBrowserMarketingEvent({ event: "PageView", event_id: "page:" + documentId.current + ":" + location.key,
        params: { page_path: publicPath } });
    } else {
      stopMarketingScripts();
      if (loaded.current) reloadMarketingDocument();
    }
  }, [config.data, consent, location.key, publicPath]);
  useEffect(() => {
    if (remoteRevision === handledRemoteRevision.current) return;
    handledRemoteRevision.current = remoteRevision;
    // Only the legacy consent-based Google integration needs a fresh document.
    if (consent !== "accepted" && document.getElementById("alfabetizando-gtm")) {
      stopMarketingScripts(); reloadMarketingDocument();
    }
  }, [remoteRevision, consent]);
  return <>{children}</>;
}
